import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, FolderOpen, X, Minus } from "lucide-react";
import Button from "./Button";
import UploadStatusIcon from "./UploadStatusIcon";
import { toast } from "react-toastify";
import { formatFileSize, buildPPFolderName, resolveUserCountryCode } from "../../utils/helpers";
import { encoderLabel } from "../../services/videoCompressionService";
import { useAuth } from "../../context/AuthContext";
import { useUpload } from "../../context/UploadContext";
import PrivacyPolicyModal, { hasAcceptedPrivacy } from "./PrivacyPolicyModal";

/**
 * Thin UI layer — the upload pipeline runs in UploadContext so it survives
 * modal minimize/unmount. This component only handles file selection and
 * displays live progress from context.
 */

let _fileIdCounter = 0;

const UploadToAlbumModal = ({ album, gallery, chatRoomId, onClose, onUploaded }) => {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { sessions, addSession, removeSession, cancelSession, startPipeline } = useUpload();
  const [localFiles, setLocalFiles] = useState([]); // files before pipeline starts
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const fileListRef = useRef(null);
  const sessionIdRef = useRef(`upload-${Date.now()}`);

  const countryCode = resolveUserCountryCode(user, null);
  const folderName = buildPPFolderName({
    baseName: gallery?.Name || album?.Name || "Upload",
    date: new Date(),
  });
  const sessionId = sessionIdRef.current;

  // Read live session from context (progress data survives minimize)
  const session = sessions[sessionId];
  const fileItems = session?.fileItems || [];
  const started = session?.started || false;

  // Use localFiles for display before pipeline starts, context fileItems after
  const displayItems = started ? fileItems : localFiles;

  // Auto-scroll file list to the active item
  useEffect(() => {
    const container = fileListRef.current;
    if (!container || !started) return;
    const activeItem = fileItems.find((i) =>
      ["checking", "compressing", "uploading", "retrying"].includes(i.status)
    );
    if (!activeItem) return;
    const el = container.querySelector(`[data-file-id="${activeItem.id}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [fileItems, started]);

  // Video extensions for which the browser may not set a MIME type (especially on Windows)
  const VIDEO_EXTS = /\.(mov|avi|mkv|wmv|webm|flv|m4v|3gp|ts|mts|mp4)$/i;

  const inferType = (file) => {
    if (file.type) return file.type;
    return VIDEO_EXTS.test(file.name) ? "video/mp4" : "image/jpeg";
  };

  const makeFileItems = (files) =>
    Array.from(files).map((file) => ({
      id: `f-${Date.now()}-${++_fileIdCounter}`,
      originalFile: file,
      processedFile: file,
      fileName: file.name,
      fileSize: file.size,
      fileType: inferType(file),
      status: "pending",
      compressProgress: 0,
      uploadProgress: 0,
      uploadMbps: 0,
      uploadPhase: "server",
      compressed: false,
      error: null,
    }));

  const handleFileChange = (e) => {
    setLocalFiles((prev) => [...prev, ...makeFileItems(e.target.files)]);
    e.target.value = ""; // reset so selecting the same files again still triggers onChange
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setLocalFiles((prev) => [...prev, ...makeFileItems(e.dataTransfer.files)]);
  };

  const removeFile = (id) => setLocalFiles((prev) => prev.filter((i) => i.id !== id));

  /** Register files in context and kick off pipeline */
  const startUpload = () => {
    addSession({
      id: sessionId,
      album,
      gallery,
      chatRoomId,
      countryCode,
      folderName,
      fileItems: localFiles,
      started: false,
      onUploaded,
    });
    startPipeline(sessionId);
  };

  const handleStart = () => {
    if (hasAcceptedPrivacy(user?.Email)) {
      startUpload();
    } else {
      setShowPrivacyModal(true);
    }
  };

  const isProcessing = started && fileItems.some(
    (i) => ["checking", "compressing", "uploading", "retrying", "pending"].includes(i.status)
  );
  const allDone = started && fileItems.length > 0 && fileItems.every((i) => i.status === "done" || i.status === "failed");
  const canStart = localFiles.length > 0 && !started;

  const doneCount = fileItems.filter((i) => i.status === "done").length;
  const failCount = fileItems.filter((i) => i.status === "failed").length;
  const imgCount = displayItems.filter((i) => i.fileType?.startsWith("image/")).length;
  const vidCount = displayItems.filter((i) => i.fileType?.startsWith("video/")).length;
  const activeItem = fileItems.find((i) => ["checking", "compressing", "uploading", "retrying"].includes(i.status));

  const formatEta = (seconds) => {
    if (seconds == null) return "";
    if (seconds < 5) return t("uploadToAlbumModal.etaFewSeconds");
    if (seconds < 60) return t("uploadToAlbumModal.etaSeconds", { seconds });
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return secs > 0 ? t("uploadToAlbumModal.etaMinutesSeconds", { minutes: mins, seconds: secs }) : t("uploadToAlbumModal.etaMinutes", { minutes: mins });
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins > 0 ? t("uploadToAlbumModal.etaHoursMinutes", { hours: hrs, minutes: remMins }) : t("uploadToAlbumModal.etaHours", { hours: hrs });
  };

  const formatMbps = (mbps) => {
    const n = Number(mbps || 0);
    if (!isFinite(n) || n <= 0) return "";
    return t("uploadToAlbumModal.mbpsSuffix", { mbps: n.toFixed(n >= 100 ? 0 : 2) });
  };

  const compressionStatusText = (item) => {
    const pct = Math.max(0, Math.min(100, Number(item.compressProgress || 0)));
    if (item.fileType?.startsWith("video/") && pct <= 30) {
      return t("uploadToAlbumModal.copyingToStudio", { percent: pct });
    }
    return t("uploadToAlbumModal.compressing", { percent: pct });
  };

  const statusText = (item) => {
    switch (item.status) {
      case "checking": return t("uploadToAlbumModal.statusChecking");
      case "compressing": return compressionStatusText(item);
      case "uploading":
        if (item.uploadPhase === "handoff") {
          return t("uploadToAlbumModal.statusPreparingUpload", { percent: item.uploadProgress, mbps: formatMbps(item.uploadMbps) });
        }
        return t("uploadToAlbumModal.statusUploading", { percent: item.uploadProgress, mbps: formatMbps(item.uploadMbps), eta: formatEta(item.eta) });
      case "retrying": return t("uploadToAlbumModal.statusRetrying");
      case "done": return item.duplicate ? t("uploadToAlbumModal.statusDuplicate") : t("actions.done");
      case "failed": return item.error || t("uploadToAlbumModal.statusFailed");
      default: return t("uploadToAlbumModal.statusReady");
    }
  };

  const [showConfirm, setShowConfirm] = useState(false);
  const [showFileDetails, setShowFileDetails] = useState(false);

  const handleCancel = () => {
    if (isProcessing) {
      setShowConfirm(true);
      return;
    }
    // If not processing, safe to remove session and close
    if (session) removeSession(sessionId);
    onClose();
  };

  const confirmCancel = () => {
    cancelSession(sessionId);
    setShowConfirm(false);
    onClose();
  };

  /** Minimize — just close modal, pipeline keeps running in context */
  const handleMinimize = () => {
    toast.info(t("uploadToAlbumModal.minimizedToast"));
    onClose();
  };

  return (
    <>
      {showPrivacyModal && (
        <PrivacyPolicyModal
          onAccept={() => { setShowPrivacyModal(false); startUpload(); }}
          onDecline={() => setShowPrivacyModal(false)}
        />
      )}
    <div style={overlayStyle} onClick={handleCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>

        {/* Confirm Cancel Dialog */}
        {showConfirm && (
          <div style={confirmOverlay} onClick={() => setShowConfirm(false)}>
            <div style={confirmBox} onClick={(e) => e.stopPropagation()}>
              <div style={{ ...confirmIconStyle, display: "flex", justifyContent: "center", color: "#d97706" }}><AlertTriangle size={26} strokeWidth={1.75} /></div>
              <h4 style={confirmTitle}>{t("uploadToAlbumModal.cancelConfirmTitle")}</h4>
              <p style={confirmMsg}>
                {t("uploadToAlbumModal.cancelConfirmMessage")}
              </p>
              <div style={confirmActions}>
                <Button variant="secondary" onClick={() => setShowConfirm(false)} style={{ flex: 1 }}>
                  {t("uploadToAlbumModal.continueUpload")}
                </Button>
                <Button onClick={confirmCancel} style={{ flex: 1, background: "#e53935" }}>
                  {t("uploadToAlbumModal.yesCancel")}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>{t("uploadToAlbumModal.title")}</h3>
            <div style={styles.subtitle}>
              {gallery.Name} / {album.Name}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {isProcessing && (
              <button style={styles.minimizeBtn} onClick={handleMinimize} title={t("uploadToAlbumModal.minimizeTitle")}>
                <Minus size={16} strokeWidth={2.5} />
              </button>
            )}
            <button style={styles.closeBtn} onClick={handleCancel}><X size={16} strokeWidth={2} /></button>
          </div>
        </div>

        {/* Drop Zone */}
        {!started && (
          <label
            style={styles.dropzone}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              type="file"
              multiple
              accept="image/*,video/*,.mov,.avi,.mkv,.wmv,.webm,.flv,.m4v,.3gp,.ts,.mts"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <div style={styles.dropIcon}><FolderOpen size={26} strokeWidth={1.75} /></div>
            <div style={styles.dropText}>
              {localFiles.length > 0
                ? t("uploadToAlbumModal.filesSelected", { count: localFiles.length })
                : t("uploadToAlbumModal.dropPrompt")}
            </div>
            {localFiles.length > 0 && (
              <div style={styles.dropSub}>
                {imgCount > 0 && t("uploadToAlbumModal.imageCount", { count: imgCount })}
                {imgCount > 0 && vidCount > 0 && "  +  "}
                {vidCount > 0 && t("uploadToAlbumModal.videoCount", { count: vidCount })}
              </div>
            )}
          </label>
        )}

        {!started && (
          <p style={styles.uploadNote}>
            {t("uploadToAlbumModal.uploadNote")}
          </p>
        )}

        {/* Progress Summary */}
        {started && (
          <div style={styles.summaryBar}>
            <span style={{ color: "#16a34a", fontWeight: 600 }}>{t("uploadToAlbumModal.doneCount", { count: doneCount })}</span>
            {failCount > 0 && <span style={{ color: "#e53935", fontWeight: 600 }}>{t("uploadToAlbumModal.failedCount", { count: failCount })}</span>}
            <span style={{ color: "#888" }}>{t("uploadToAlbumModal.totalCount", { count: fileItems.length })}</span>
          </div>
        )}

        {/* Current file mini-status (when details hidden & uploading) */}
        {started && activeItem && !showFileDetails && (
          <div style={styles.activeFileMini}>
            <span style={{ display: "inline-flex" }}><UploadStatusIcon status={activeItem.status} fileType={activeItem.fileType} isDuplicate={activeItem.duplicate} size={13} /></span>
            <span style={styles.activeFileName}>{activeItem.fileName}</span>
            <span style={styles.activeFileStatus}>{statusText(activeItem)}</span>
          </div>
        )}

        {/* Toggle / File List */}
        {displayItems.length > 0 && (
          <>
            {started && (
              <button
                style={styles.toggleBtn}
                onClick={() => setShowFileDetails((v) => !v)}
              >
                {showFileDetails ? t("uploadToAlbumModal.hideDetails") : t("uploadToAlbumModal.showDetails", { count: displayItems.length })}
              </button>
            )}
            {(!started || showFileDetails) && (
              <div style={styles.fileList} ref={fileListRef}>
                {displayItems.map((item) => (
                  <div key={item.id} data-file-id={item.id} style={{
                    ...styles.fileRow,
                    background: item.status === "done" ? "#f0fdf4" : item.status === "failed" ? "#fef2f2" : "#fff",
                  }}>
                    <span style={styles.fileTypeTag}><UploadStatusIcon status={item.status} fileType={item.fileType} isDuplicate={item.duplicate} /></span>

                    <div style={styles.fileInfo}>
                      <span style={styles.fileName}>{item.fileName}</span>

                      {/* Progress bars */}
                      {item.status === "compressing" && (
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.barFill, width: `${item.compressProgress}%`, background: "#f59e0b" }} />
                        </div>
                      )}
                      {item.status === "uploading" && (
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.barFill, width: `${item.uploadProgress}%`, background: "#6c63ff" }} />
                        </div>
                      )}

                      <span style={{
                        ...styles.statusLabel,
                        color: item.status === "done" ? "#16a34a" : item.status === "failed" ? "#e53935" : "#888",
                      }}>
                        {statusText(item)}
                      </span>
                                          {item.compressEngine && (
                                            <span style={{
                                              display: "inline-block",
                                              marginTop: 2,
                                              fontSize: 10,
                                              fontWeight: 600,
                                              color: item.compressEngine === "libx264" ? "#888" : "#6c63ff",
                                              background: item.compressEngine === "libx264" ? "#f3f4f6" : "#ede9fe",
                                              borderRadius: 4,
                                              padding: "1px 6px",
                                            }}>
                                              {encoderLabel(item.compressEngine)}
                                            </span>
                                          )}
                    </div>

                    <span style={styles.fileSize}>{formatFileSize(item.fileSize)}</span>

                    {!started && (
                      <button style={styles.removeBtn} onClick={() => removeFile(item.id)}><X size={12} strokeWidth={2.25} /></button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div style={styles.actions}>
          <Button variant="secondary" onClick={handleCancel} type="button">
            {allDone ? t("actions.close") : t("actions.cancel")}
          </Button>
          {isProcessing && (
            <Button variant="secondary" onClick={handleMinimize} type="button">
              {t("uploadToAlbumModal.minimize")}
            </Button>
          )}
          {canStart && (
            <Button onClick={handleStart}>
              {t("uploadToAlbumModal.startUpload", { count: localFiles.length })}
            </Button>
          )}
          {isProcessing && (
            <Button disabled>{t("uploadToAlbumModal.processing")}</Button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

const overlayStyle = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "#fff",
  borderRadius: 16,
  padding: "28px 32px",
  width: "100%",
  maxWidth: 500,
  boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
  maxHeight: "90vh",
  overflowY: "auto",
  position: "relative",
};

const styles = {
  header: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start", marginBottom: 20,
  },
  title: { margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  subtitle: { fontSize: 13, color: "#888" },
  closeBtn: {
    background: "none", border: "none", fontSize: 18,
    cursor: "pointer", color: "#888", lineHeight: 1,
  },
  minimizeBtn: {
    background: "none", border: "none", fontSize: 18,
    cursor: "pointer", color: "#6c63ff", lineHeight: 1,
    fontWeight: 700,
  },

  dropzone: {
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    border: "2px dashed #c0b9ff", borderRadius: 12,
    padding: "28px 20px", cursor: "pointer",
    background: "#f9f8ff", marginBottom: 16, textAlign: "center",
  },
  dropIcon: { fontSize: 28, marginBottom: 8 },
  dropText: { fontSize: 14, color: "#6c63ff", fontWeight: 600, marginBottom: 4 },
  dropSub: { fontSize: 12, color: "#888" },
  uploadNote: {
    margin: "0 2px 16px",
    fontSize: 12,
    lineHeight: 1.5,
    color: "#666",
  },

  summaryBar: {
    display: "flex", gap: 12, alignItems: "center",
    padding: "10px 14px", background: "#f9fafb",
    borderRadius: 10, marginBottom: 14, fontSize: 13,
  },

  fileList: {
    maxHeight: 280, overflowY: "auto",
    border: "1px solid #f0f0f0", borderRadius: 8, marginBottom: 16,
  },
  fileRow: {
    display: "flex", alignItems: "center",
    gap: 8, padding: "8px 12px",
    borderBottom: "1px solid #f5f5f5", fontSize: 13,
  },
  fileTypeTag: { fontSize: 14, flexShrink: 0 },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: {
    display: "block", color: "#333",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    marginBottom: 2,
  },
  statusLabel: { fontSize: 11, color: "#888", display: "block", marginTop: 2 },
  barWrap: {
    height: 4, background: "#f0f0f0", borderRadius: 2,
    overflow: "hidden", marginTop: 4, marginBottom: 2,
  },
  barFill: {
    height: "100%", borderRadius: 2,
    transition: "width 0.2s ease",
  },
  fileSize: { color: "#aaa", flexShrink: 0, fontSize: 11, minWidth: 44, textAlign: "right" },
  removeBtn: {
    background: "none", border: "none",
    cursor: "pointer", color: "#bbb",
    fontSize: 13, flexShrink: 0, padding: "0 2px",
  },
  activeFileMini: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "6px 10px", background: "#f9f8ff",
    borderRadius: 6, marginBottom: 6, overflow: "hidden",
  },
  activeFileName: {
    fontSize: 12, fontWeight: 600, color: "#333",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    flex: 1, minWidth: 0,
  },
  activeFileStatus: {
    fontSize: 11, color: "#6c63ff", fontWeight: 600,
    whiteSpace: "nowrap", flexShrink: 0,
  },
  toggleBtn: {
    background: "#f5f3ff", border: "1px solid #e0daf7",
    fontSize: 12, color: "#6c63ff", cursor: "pointer",
    padding: "6px 14px", fontWeight: 600, width: "100%",
    textAlign: "center", borderRadius: 6, marginBottom: 8,
  },

  actions: { display: "flex", justifyContent: "flex-end", gap: 12 },

  qualityRow: { marginBottom: 16 },
  qualityLabel: { fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 8 },
  qualityOptions: { display: "flex", gap: 8 },
  qualityBtn: {
    flex: 1, padding: "8px 6px", borderRadius: 8, cursor: "pointer",
    borderWidth: 2, borderStyle: "solid", borderColor: "#e5e7eb",
    background: "#fafafa", textAlign: "center",
    transition: "all 0.15s",
  },
  qualityBtnActive: {
    borderColor: "#7C3AED", background: "#f5f3ff",
  },
  qualityBtnLabel: { display: "block", fontSize: 13, fontWeight: 600, color: "#333" },
  qualityBtnSub: { display: "block", fontSize: 10, color: "#888", marginTop: 2 },
};

const confirmOverlay = {
  position: "absolute", inset: 0, zIndex: 10,
  background: "rgba(0,0,0,0.35)", borderRadius: 16,
  display: "flex", alignItems: "center", justifyContent: "center",
};
const confirmBox = {
  background: "#fff", borderRadius: 14, padding: "28px 24px",
  width: "85%", maxWidth: 340, textAlign: "center",
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
};
const confirmIconStyle = { fontSize: 36, marginBottom: 8 };
const confirmTitle = { margin: "0 0 8px", fontSize: 17, fontWeight: 700, color: "#1e1e2d" };
const confirmMsg = { margin: "0 0 20px", fontSize: 13, color: "#666", lineHeight: 1.5 };
const confirmActions = { display: "flex", gap: 10 };

export default UploadToAlbumModal;
